/**
 * 性能预算（M5-01 / DESIGN.md §8）。
 *
 * §8 的表格是产品预算，不是测试断言的私有常量——这里把它翻成一份代码里
 * 唯一的数字来源，`PerfPanel`（实时读数）和 `test/app/perf-metrics.test.ts`
 * （自动化测量）都从这里取目标/硬上限，避免两处各自抄一份数字，改一处漏一处。
 *
 * 这个文件本身只有预算数字+纯换算，浏览器/Node 都能安全 import——
 * `PerfPanel.tsx`（浏览器）和测试文件都从这里取数。真正跑 CPU 计时的
 * `timeCpuBestOf`/`isSystemBusy` 这类依赖 `process`/`node:os` 的工具函数
 * 放在 `metrics-node.ts`，只给测试用：早先把它们跟这个文件放一起，
 * Vite 生产构建会把 `node:os` 当成浏览器不认识的模块外部化并发警告——
 * `PerfPanel.tsx` 从没调用过那几个函数，但只要同一个文件里 import 了
 * Node 模块，构建工具就会把它一起打进浏览器 bundle，这是构建产物里的
 * 死代码/警告，不是"能用但丑"，拆开是唯一干净的做法。
 */
import { DEFAULT_HISTORY_BUDGET_BYTES } from '../history';

export interface BudgetRange {
  /** §8"目标"列。 */
  readonly target: number;
  /** §8"硬上限"列——超过判定失败，不只是警告。 */
  readonly hard: number;
  readonly unit: string;
}

export const PERF_BUDGET = {
  ttiMs: { target: 2000, hard: 4000, unit: 'ms' },
  bundleGzipKb: { target: 4096, hard: 8192, unit: 'KB' },
  openLargeJpegMs: { target: 800, hard: 2000, unit: 'ms' },
  brushLatencyMs: { target: 16, hard: 33, unit: 'ms' },
  viewportFps: { target: 60, hard: 30, unit: 'fps' },
  filterPreviewMs: { target: 33, hard: 100, unit: 'ms' },
  residentMemoryMb: { target: 600, hard: 1200, unit: 'MB' },
  historyBudgetBytes: {
    target: DEFAULT_HISTORY_BUDGET_BYTES,
    hard: DEFAULT_HISTORY_BUDGET_BYTES,
    unit: 'bytes',
  },
} satisfies Record<string, BudgetRange>;

/** fps 预算换算成"每帧最多能花多少 ms"——`viewportFps` 是"越大越好"，
 * 跟其余"越小越好"的预算方向相反，这里统一成时间维度，直接跟 CPU 耗时比。 */
export function fpsBudgetToFrameMs(budget: BudgetRange): BudgetRange {
  return { target: 1000 / budget.target, hard: 1000 / budget.hard, unit: 'ms' };
}
